import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, map, of } from 'rxjs';
import {
  ORGANIZATION_REQUEST_TYPES,
  OrganizationRequest,
  OrganizationRequestDocument,
} from '../../../models/organization-request.model';
import { OrganizationRequestService } from '../../../services/organization-request.service';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';

/**
 * Platform admins review requests to create organizations: check the details and documents, then
 * approve (the server creates the organization with the requester as admin) or reject with a reason.
 * Access needs the `platformAdmin` custom claim; Firestore and Storage rules enforce it too.
 */
@Component({
  selector: 'app-org-requests-review',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, BtnBackComponent],
  templateUrl: './org-requests-review.component.html',
})
export class OrgRequestsReviewComponent implements OnInit {
  private requests = inject(OrganizationRequestService);
  private toastr = inject(ToastrService);
  private logger = inject(NGXLogger);

  // null while checking the claim
  isPlatformAdmin: boolean | null = null;
  showReviewed = false;
  requests$: Observable<OrganizationRequest[]> = of([]);

  openRequestId: string | null = null;
  notes: Record<string, string> = {};
  reviewingId: string | null = null;

  async ngOnInit(): Promise<void> {
    this.isPlatformAdmin = await this.requests.isPlatformAdmin();
    this.logger.debug('org-requests-review.component.ts :: Platform admin check:', { isPlatformAdmin: this.isPlatformAdmin });
    if (this.isPlatformAdmin) {
      this.requests$ = this.requests.getRequestsForReview().pipe(
        // Pending first, then most recent
        map((list) => [...list].sort((a, b) => Number(b.status === 'pending') - Number(a.status === 'pending')))
      );
    }
  }

  visible(list: OrganizationRequest[]): OrganizationRequest[] {
    return this.showReviewed ? list : list.filter((r) => r.status === 'pending');
  }

  pendingCount(list: OrganizationRequest[]): number {
    return list.filter((r) => r.status === 'pending').length;
  }

  toggleShowReviewed(): void {
    this.showReviewed = !this.showReviewed;
    this.logger.debug('org-requests-review.component.ts :: Show reviewed toggled:', { showReviewed: this.showReviewed });
  }

  toggleOpen(request: OrganizationRequest): void {
    this.openRequestId = this.openRequestId === request.id ? null : request.id;
    this.logger.debug('org-requests-review.component.ts :: Request clicked:', { requestId: request.id, open: !!this.openRequestId });
  }

  async openDocument(doc: OrganizationRequestDocument): Promise<void> {
    this.logger.debug('org-requests-review.component.ts :: Open document clicked:', { path: doc.storagePath });
    // Open the tab synchronously so pop-up blockers allow it, then point it at the file
    const tab = window.open('', '_blank');
    try {
      const url = await this.requests.documentUrl(doc.storagePath);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      tab?.close();
      this.logger.error('org-requests-review.component.ts :: Opening document failed:', { path: doc.storagePath, err });
      this.toastr.error('Could not open the document.');
    }
  }

  async review(request: OrganizationRequest, approve: boolean): Promise<void> {
    const note = (this.notes[request.id] || '').trim();
    this.logger.debug('org-requests-review.component.ts :: Review clicked:', { requestId: request.id, approve });
    if (!approve && !note) {
      this.toastr.warning('Add a reason so the requester knows what to fix.');
      return;
    }
    this.reviewingId = request.id;
    try {
      const result = await this.requests.reviewRequest(request.id, approve, note);
      this.logger.debug('org-requests-review.component.ts :: Request reviewed:', { requestId: request.id, ...result });
      this.toastr.success(approve ? `"${request.name}" approved and created.` : `"${request.name}" rejected.`);
      this.openRequestId = null;
    } catch (err: any) {
      this.logger.error('org-requests-review.component.ts :: Reviewing request failed:', { requestId: request.id, approve, err });
      this.toastr.error(err?.message || 'Could not save the review.');
    } finally {
      this.reviewingId = null;
    }
  }

  typeLabel(value: string): string {
    return ORGANIZATION_REQUEST_TYPES.find((t) => t.value === value)?.label || value;
  }
}
